<script setup lang="ts">
/**
 * Inviting a family, and getting the link to them (SPEC §6.6).
 *
 * Three ways to send the same invitation, because an organizer already has a
 * way of talking to this family and the product's job is to get out of it:
 * copy it, hand it to the device's own share sheet, or open the text message
 * composer with the words already written. The words come from the server so
 * that all three say the same thing.
 *
 * The link appears once, when the invitation is created. Only its hash is
 * stored, so there is nothing to show it again from — losing it means creating
 * another invitation, which is why that is one button.
 */
import { computed, ref } from 'vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import {
  ApiError,
  endpoints,
  type CreatedHouseholdInviteResponse,
  type HouseholdInviteResponse,
} from '@/api'

const props = defineProps<{
  groupId: string
  householdId: string
  householdName: string
}>()

const invitations = ref<HouseholdInviteResponse[]>([])
const created = ref<CreatedHouseholdInviteResponse | null>(null)
const phone = ref('')
const forwardMyself = ref(false)
const loading = ref(true)
const busy = ref(false)
const error = ref<string | null>(null)
const copied = ref(false)

const canShare = computed(() => typeof navigator !== 'undefined' && 'share' in navigator)

/** `sms:` with the body prefilled. iOS wants `&`, everything else wants `?`. */
const smsHref = computed(() =>
  created.value ? `sms:?&body=${encodeURIComponent(created.value.messageText)}` : '',
)

const open = computed(() => invitations.value.filter((invitation) => invitation.isOpen))

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

async function load() {
  loading.value = true

  try {
    invitations.value = await endpoints.listHouseholdInvitations(props.groupId, props.householdId)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

async function create() {
  busy.value = true
  error.value = null
  copied.value = false

  try {
    created.value = await endpoints.createHouseholdInvitation(props.groupId, props.householdId, {
      phone: phone.value.trim() || null,
      forwardMyself: forwardMyself.value,
    })

    phone.value = ''
    forwardMyself.value = false

    await load()
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = false
  }
}

/*
  Withdrawing an invitation is destructive in the way that matters: the link is
  already out there, in somebody's text messages, and this is what stops it
  working. SPEC §9.1 asks for the pause.
*/
const revoking = ref<string | null>(null)

async function revoke(): Promise<void> {
  const inviteId = revoking.value
  if (!inviteId) return

  busy.value = true
  error.value = null

  try {
    await endpoints.revokeHouseholdInvitation(props.groupId, props.householdId, inviteId)
    revoking.value = null
    await load()
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = false
  }
}

async function copy() {
  if (!created.value) return

  try {
    await navigator.clipboard.writeText(created.value.messageText)
    copied.value = true
  } catch {
    // A browser that refuses clipboard access is not an error worth showing:
    // the message is on screen and selectable.
    copied.value = false
  }
}

async function share() {
  if (!created.value) return

  try {
    await navigator.share({ text: created.value.messageText })
  } catch {
    // Includes the person changing their mind, which is not a failure.
  }
}

void load()
</script>

<template>
  <v-card variant="outlined">
    <v-card-item>
      <v-card-title tag="h2" class="invite__title">Invite this family</v-card-title>
      <v-card-subtitle class="invite__subtitle">
        Send {{ householdName }} a link. They tap it and they are in — no account, no password.
      </v-card-subtitle>
    </v-card-item>

    <v-card-text>
      <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

      <div v-if="created" class="invite__ready">
        <v-alert type="success" variant="tonal" class="mb-4">
          Here is the invitation. It is shown once, so send it now — you can always create another.
        </v-alert>

        <p class="invite__message">{{ created.messageText }}</p>

        <div class="invite__actions">
          <v-btn variant="flat" color="primary" @click="copy">
            {{ copied ? 'Copied' : 'Copy message' }}
          </v-btn>
          <v-btn v-if="canShare" variant="outlined" @click="share">Share</v-btn>
          <v-btn variant="outlined" :href="smsHref">Text it</v-btn>
          <v-btn variant="text" @click="created = null">Done</v-btn>
        </div>
      </div>

      <form v-else class="invite__form" @submit.prevent="create">
        <v-text-field
          v-model="phone"
          label="Their mobile number"
          placeholder="+15551234567"
          hint="We never store the number itself, only a way to recognise it."
          persistent-hint
          :disabled="forwardMyself || busy"
          autocomplete="tel"
        />

        <v-checkbox
          v-model="forwardMyself"
          label="I don't have their number — create a link I'll forward myself"
          :disabled="busy"
          hide-details
        />

        <p class="invite__warning">
          A link with no number attached works for anybody who opens it. Send it only to the person
          it is meant for.
        </p>

        <v-btn
          type="submit"
          variant="flat"
          color="primary"
          :loading="busy"
          :disabled="!phone.trim() && !forwardMyself"
        >
          Create invitation
        </v-btn>
      </form>

      <template v-if="!loading && open.length > 0">
        <v-divider class="my-6" />

        <h3 class="invite__outstanding">Waiting to be used</h3>
        <ul class="invite__list">
          <li v-for="invitation in open" :key="invitation.id" class="invite__item">
            <span>
              Sent {{ new Date(invitation.createdUtc).toLocaleDateString() }}, expires
              {{ new Date(invitation.expiresUtc).toLocaleDateString() }}
              <template v-if="!invitation.boundToPhone"> — anyone with the link</template>
            </span>
            <v-btn variant="text" size="small" :disabled="busy" @click="revoking = invitation.id">
              Withdraw
            </v-btn>
          </li>
        </ul>
      </template>
    </v-card-text>

    <ConfirmDialog
      :model-value="revoking !== null"
      title="Withdraw this invitation?"
      body="The link stops working, so anyone still holding it cannot join. You can create a new invitation afterwards."
      confirm-label="Withdraw it"
      cancel-label="Leave it working"
      :busy="busy"
      destructive
      @update:model-value="revoking = null"
      @confirm="revoke"
    />
  </v-card>
</template>

<style scoped>
.invite__title {
  font-size: 1.25rem;
  font-weight: 600;
}

.invite__subtitle {
  white-space: normal;
  opacity: 1;
  color: rgb(var(--v-theme-on-surface));
}

.invite__message {
  margin: 0 0 1rem;
  overflow-wrap: anywhere;
}

.invite__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.invite__warning {
  margin: 0.5rem 0 1.25rem;
  max-width: 44ch;
  font-size: 0.9375rem;
}

.invite__outstanding {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.invite__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.invite__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem 0;
}

.invite__item + .invite__item {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
